import { DatePipe, JsonPipe, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { ConsultingReservationService } from 'src/app/components/consulting-reservation';
import { MapDialogComponent } from 'src/app/components/consulting-reservation/map-dialog/map-dialog.component';
import { createModalCounselingReservationDialogForm } from 'src/app/components/modal-counseling-reservation/modal-counseling-reservation-dialog/modal-counseling-reservation-dialog.viewmodel';
import { ScheduleService } from 'src/app/features/schedule';
import { Schedule } from 'src/app/features/schedule/schedule.types';

interface DateData {
  date: string | Date;
  time: string;
}

type Mode = 'reserve' | 'complete';

@Component({
  selector: 'app-modal-counseling-reservation-dialog',
  imports: [
    DatePipe,
    SlicePipe,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatRadioModule
  ],
  templateUrl: './modal-counseling-reservation-dialog.component.html',
  styleUrl: './modal-counseling-reservation-dialog.component.scss',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalCounselingReservationDialogComponent {
  readonly #datePipe = inject(DatePipe);
  readonly #dialog = inject(MatDialog);
  readonly #scheduleService = inject(ScheduleService);
  readonly #reservationService = inject(ConsultingReservationService);
  readonly data = inject(MAT_DIALOG_DATA);

  readonly form = createModalCounselingReservationDialogForm();

  readonly place = signal<string>('新宿オフィス');
  readonly schedules = signal<Schedule[]>([]);
  readonly sending = signal<boolean>(false);
  readonly selectedDate = signal<DateData | null>(null);
  readonly mode = signal<Mode>('reserve');
  readonly canPrev = computed<boolean>(() => {
    const schedules = this.schedules();
    if (schedules.length === 0) {
      return false;
    }
    const date = new Date(this.schedules()[0].day);
    return date > new Date();
  });

  constructor() {
    const session = sessionStorage.getItem('counseling');
    let date: string | undefined = undefined;
    if (session) {
      const value = JSON.parse(session);
      this.place.set(value.place);
      date = this.#datePipe.transform(new Date(value.startTime), 'yyyy-MM-dd')!;
    }

    this.#scheduleService.get(date)
      .subscribe(res => {
        this.schedules.set(res);
      });
  }

  prevWeek(): void {
    if (!this.canPrev()) {
      return;
    }
    const date = new Date(this.schedules()[0].day);
    const target = this.#datePipe.transform(date.setDate(date.getDate() - 7), 'yyyy-MM-dd')!;
    this.#scheduleService.get(target)
      .subscribe(res => {
        this.schedules.set(res);
      });
  }

  nextWeek(): void {
    const date = new Date(this.schedules()[0].day);
    const target = this.#datePipe.transform(date.setDate(date.getDate() + 7), 'yyyy-MM-dd')!;
    this.#scheduleService.get(target)
      .subscribe(res => {
        this.schedules.set(res);
      });
  }

  openMap(): void {
    this.#dialog.open(MapDialogComponent, {
      width: '86rem',
      maxWidth: '98%',
      maxHeight: '95dvh',
    });
  }

  select(target: string | Date, time: string): void {
    this.selectedDate.set({ date: target, time });
  }

  submit(): void {
    this.form.markAllAsTouched();
    const selectedDate = this.selectedDate();
    if (this.form.invalid || selectedDate === null) {
      return;
    }

    const value = JSON.parse(sessionStorage.getItem('shindan-form') ?? '{}');
    const times = selectedDate!.time.split(':');
    const target = new Date(selectedDate!.date);
    target.setHours(Number(times[0]));
    target.setMinutes(Number(times[1]));
    Object.assign(value, { startTime: target, place: this.place() });

    this.sending.set(true);

    this.#reservationService.reserve(value)
      .subscribe({
        next: res => {
          if (res === null) {
            return;
          }
          sessionStorage.removeItem('shindan-form');
          //this.mode.set('complete');
          //setTimeout(() => {
          window.location.href = `/ab/counseling-complete?no=${res.id}`;
          //}, 6000);
        },
        error: error => {
          if (error.status === 409) {
            alert('既にお申込みがあります。\n\nご予約内容（お名前やメールアドレス）に不備があった場合や、カウンセリング日時の確認・変更をご希望の場合は、夢カナ留学の下記電話番号までお問い合わせください。\n\n03-5050-0802');
          }
        }
      })
      .add(() => this.sending.set(false));
  }
}
